/**
 * Connection ledger — every NATS client this tab has opened through
 * `connectSingleton`, with its lifecycle, so the devtool can answer "how many
 * sockets do we hold right now, and why". One open client is the design; a
 * second one that outlives an identity flip or a rebuild is a leak, and the
 * ledger is where it shows.
 *
 * Pinned on `globalThis`: an HMR module swap must not forget a client opened
 * by an earlier module instance — that is exactly the leak worth seeing.
 * Platform-neutral: only the connection object's own methods are read.
 */
import { CONNECTION_LEDGER_GLOBAL_KEY } from './globals.js';
import { reportNatsFailure, reportNatsRecovery } from './report-nats-failure.js';

export type SdkConnectionState =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'draining'
  | 'closed'
  | 'failed';

export type SdkConnectionIdentity = 'anon' | 'user' | 'unknown';

export interface SdkConnectionRecord {
  readonly id: number;
  readonly state: SdkConnectionState;
  /** Credential kind the client was opened under. */
  readonly identity: SdkConnectionIdentity;
  /** Configured server URL. */
  readonly url: string;
  /** Server the client actually landed on (after any reconnect). */
  readonly server: string | null;
  readonly startedAt: number;
  readonly connectedAt: number | null;
  readonly closedAt: number | null;
  readonly closeReason: string | null;
  /** For a closed client: retired by the SDK on purpose, or dropped on us. */
  readonly intentional: boolean | null;
  /** nats-core's own reconnects on this client (same client, new socket). */
  readonly reconnects: number;
  readonly disconnects: number;
  readonly lastStatus: string | null;
  readonly lastStatusAt: number | null;
  readonly inMsgs: number;
  readonly outMsgs: number;
  readonly inBytes: number;
  readonly outBytes: number;
}

export interface SdkConnectionLedgerSnapshot {
  /** Clients currently holding a socket (connecting, connected, reconnecting or draining). */
  readonly open: number;
  /** Since when more than one client has been open; `null` while at most one is. */
  readonly overlapSince: number | null;
  /** Leak reports fired for the current overlap (0 inside the grace window). */
  readonly overlapReports: number;
  readonly peakOpen: number;
  readonly attempts: number;
  readonly opened: number;
  readonly failed: number;
  readonly closed: number;
  readonly unexpectedCloses: number;
  readonly reconnects: number;
  /** Newest first. */
  readonly connections: readonly SdkConnectionRecord[];
}

/** The subset of a nats-core connection the ledger reads. */
export interface LedgerConnectionLike {
  closed?(): Promise<void | Error>;
  status?(): AsyncIterable<{ readonly type?: unknown }>;
  getServer?(): string;
  stats?(): {
    readonly inMsgs?: number;
    readonly outMsgs?: number;
    readonly inBytes?: number;
    readonly outBytes?: number;
  };
}

export interface ConnectionAttempt {
  readonly id: number;
  connected(nc: LedgerConnectionLike): void;
  failed(err: unknown): void;
}

interface Entry {
  readonly id: number;
  state: SdkConnectionState;
  readonly identity: SdkConnectionIdentity;
  readonly url: string;
  server: string | null;
  readonly startedAt: number;
  connectedAt: number | null;
  closedAt: number | null;
  closeReason: string | null;
  intentional: boolean | null;
  reconnects: number;
  disconnects: number;
  lastStatus: string | null;
  lastStatusAt: number | null;
  nc: LedgerConnectionLike | null;
}

interface LedgerState {
  nextId: number;
  readonly entries: Map<number, Entry>;
  readonly byConnection: WeakMap<object, Entry>;
  readonly listeners: Set<() => void>;
  peakOpen: number;
  attempts: number;
  opened: number;
  failed: number;
  closed: number;
  unexpectedCloses: number;
  overlapSince: number | null;
  overlapReports: number;
  overlapTimer: ReturnType<typeof setTimeout> | null;
  overlapGraceMs: number;
}

/** Records kept (open ones are never evicted). */
const MAX_RECORDS = 50;

/**
 * An identity flip or a recovery rebuild legitimately holds two clients for
 * a moment (the old one drains while the new one connects). An overlap that
 * outlives this grace is a leaked socket and is reported as an error.
 */
export const CONNECTION_OVERLAP_GRACE_MS = 5_000;

/** While a leak persists, repeat the report at this cadence. */
const CONNECTION_OVERLAP_REPEAT_MS = 60_000;

type GlobalWithLedger = typeof globalThis & { [CONNECTION_LEDGER_GLOBAL_KEY]?: LedgerState };

function state(): LedgerState {
  const g = globalThis as GlobalWithLedger;
  g[CONNECTION_LEDGER_GLOBAL_KEY] ??= freshState();
  return upgradePin(g[CONNECTION_LEDGER_GLOBAL_KEY]);
}

/**
 * The pin outlives module swaps on purpose, so it may predate fields added
 * by a newer module version; backfill them instead of reading `undefined`.
 */
function upgradePin(st: LedgerState): LedgerState {
  const partial = st as { -readonly [K in keyof LedgerState]?: LedgerState[K] };
  partial.overlapSince ??= null;
  partial.overlapReports ??= 0;
  partial.overlapTimer ??= null;
  partial.overlapGraceMs ??= CONNECTION_OVERLAP_GRACE_MS;
  return st;
}

function freshState(): LedgerState {
  return {
    nextId: 1,
    entries: new Map(),
    byConnection: new WeakMap(),
    listeners: new Set(),
    peakOpen: 0,
    attempts: 0,
    opened: 0,
    failed: 0,
    closed: 0,
    unexpectedCloses: 0,
    overlapSince: null,
    overlapReports: 0,
    overlapTimer: null,
    overlapGraceMs: CONNECTION_OVERLAP_GRACE_MS,
  };
}

function isOpen(s: SdkConnectionState): boolean {
  return s === 'connecting' || s === 'connected' || s === 'reconnecting' || s === 'draining';
}

function openCount(st: LedgerState): number {
  let n = 0;
  for (const e of st.entries.values()) if (isOpen(e.state)) n += 1;
  return n;
}

function notify(st: LedgerState): void {
  checkOverlap(st);
  for (const cb of st.listeners) {
    try {
      cb();
    } catch (err) {
      console.error('[morfi-sdk] connection ledger listener threw:', err);
    }
  }
}

function checkOverlap(st: LedgerState): void {
  if (openCount(st) > 1) {
    if (st.overlapSince === null) {
      st.overlapSince = Date.now();
      scheduleOverlapReport(st, st.overlapGraceMs);
    }
    return;
  }
  if (st.overlapTimer !== null) {
    clearTimeout(st.overlapTimer);
    st.overlapTimer = null;
  }
  if (st.overlapSince !== null && st.overlapReports > 0) {
    reportNatsRecovery({
      stage: 'connection-count',
      code: 'NATS_CONNECTION_LEAK_RESOLVED',
      attempts: st.overlapReports,
      downtimeMs: Math.max(0, Date.now() - st.overlapSince),
    });
  }
  st.overlapSince = null;
  st.overlapReports = 0;
}

function scheduleOverlapReport(st: LedgerState, delayMs: number): void {
  st.overlapTimer = setTimeout(() => {
    st.overlapTimer = null;
    if (st.overlapSince === null || openCount(st) <= 1) {
      checkOverlap(st);
      return;
    }
    st.overlapReports += 1;
    const openEntries = [...st.entries.values()].filter((e) => isOpen(e.state));
    const now = Date.now();
    const seconds = ((now - st.overlapSince) / 1000).toFixed(1);
    const detail = openEntries
      .map((e) => `#${e.id} ${e.identity} ${e.state} ${Math.round((now - e.startedAt) / 1000)}s`)
      .join(', ');
    const natsUrl = openEntries.at(-1)?.url;
    reportNatsFailure({
      phase: 'leak',
      stage: 'connection-count',
      code: 'NATS_CONNECTION_LEAK',
      reason: 'multiple-clients',
      message: `${openEntries.length} clients open for ${seconds}s (one is the design): ${detail}`,
      isAuth: false,
      attempt: st.overlapReports,
      nextRetryMs: CONNECTION_OVERLAP_REPEAT_MS,
      ...(natsUrl === undefined ? {} : { natsUrl }),
    });
    scheduleOverlapReport(st, CONNECTION_OVERLAP_REPEAT_MS);
  }, delayMs);
}

function evict(st: LedgerState): void {
  if (st.entries.size <= MAX_RECORDS) return;
  for (const [id, e] of st.entries) {
    if (st.entries.size <= MAX_RECORDS) break;
    if (!isOpen(e.state)) st.entries.delete(id);
  }
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message === '' ? err.name : `${err.name}: ${err.message}`;
  return String(err);
}

/** Start a record for a connect attempt; resolve it with `connected` or `failed`. */
export function beginConnection(url: string, identity: SdkConnectionIdentity): ConnectionAttempt {
  const st = state();
  const entry: Entry = {
    id: st.nextId,
    state: 'connecting',
    identity,
    url,
    server: null,
    startedAt: Date.now(),
    connectedAt: null,
    closedAt: null,
    closeReason: null,
    intentional: null,
    reconnects: 0,
    disconnects: 0,
    lastStatus: null,
    lastStatusAt: null,
    nc: null,
  };
  st.nextId += 1;
  st.attempts += 1;
  st.entries.set(entry.id, entry);
  evict(st);
  st.peakOpen = Math.max(st.peakOpen, openCount(st));
  notify(st);
  return {
    id: entry.id,
    connected: (nc): void => {
      entry.state = 'connected';
      entry.connectedAt = Date.now();
      entry.nc = nc;
      entry.server = readServer(nc);
      st.byConnection.set(nc, entry);
      st.opened += 1;
      st.peakOpen = Math.max(st.peakOpen, openCount(st));
      void watchStatus(st, entry, nc);
      watchClosed(st, entry, nc);
      notify(st);
    },
    failed: (err): void => {
      if (entry.state !== 'connecting') return;
      entry.state = 'failed';
      entry.closedAt = Date.now();
      entry.closeReason = describe(err);
      st.failed += 1;
      notify(st);
    },
  };
}

/** The SDK is about to drain/close this client on purpose. */
export function noteConnectionDraining(nc: object): void {
  const st = state();
  const entry = st.byConnection.get(nc);
  if (entry === undefined || !isOpen(entry.state)) return;
  entry.state = 'draining';
  entry.intentional = true;
  notify(st);
}

function settleClosed(st: LedgerState, entry: Entry, err: unknown): void {
  if (entry.state === 'closed') return;
  const intentional = entry.intentional === true;
  entry.state = 'closed';
  entry.closedAt = Date.now();
  entry.intentional = intentional;
  entry.closeReason =
    err === undefined || err === null ? (intentional ? 'closed by sdk' : 'closed') : describe(err);
  entry.nc = null;
  st.closed += 1;
  if (!intentional) st.unexpectedCloses += 1;
  notify(st);
}

function watchClosed(st: LedgerState, entry: Entry, nc: LedgerConnectionLike): void {
  if (typeof nc.closed !== 'function') return;
  try {
    void nc.closed().then(
      (err) => settleClosed(st, entry, err),
      (err) => settleClosed(st, entry, err),
    );
  } catch {
    // A connection that cannot report its close stays "connected" in the ledger.
  }
}

function readServer(nc: LedgerConnectionLike): string | null {
  try {
    const server = nc.getServer?.();
    return typeof server === 'string' && server !== '' ? server : null;
  } catch {
    return null;
  }
}

async function watchStatus(st: LedgerState, entry: Entry, nc: LedgerConnectionLike): Promise<void> {
  if (typeof nc.status !== 'function') return;
  try {
    for await (const status of nc.status()) {
      const type = typeof status.type === 'string' ? status.type : 'unknown';
      entry.lastStatus = type;
      entry.lastStatusAt = Date.now();
      if (type === 'disconnect') {
        entry.disconnects += 1;
        if (entry.state === 'connected') entry.state = 'reconnecting';
      } else if (type === 'reconnecting') {
        if (entry.state === 'connected') entry.state = 'reconnecting';
      } else if (type === 'reconnect') {
        entry.reconnects += 1;
        if (entry.state === 'reconnecting') entry.state = 'connected';
        entry.server = readServer(nc) ?? entry.server;
      } else {
        // Pings and cluster updates are traffic, not lifecycle: no listener churn.
        continue;
      }
      notify(st);
    }
  } catch {
    // The status iterator ends with the connection; `closed()` settles the record.
  }
}

export function getConnectionLedgerSnapshot(): SdkConnectionLedgerSnapshot {
  const st = state();
  const connections: SdkConnectionRecord[] = [];
  let reconnects = 0;
  for (const e of st.entries.values()) {
    reconnects += e.reconnects;
    const stats = readStats(e.nc);
    connections.push({
      id: e.id,
      state: e.state,
      identity: e.identity,
      url: e.url,
      server: e.server,
      startedAt: e.startedAt,
      connectedAt: e.connectedAt,
      closedAt: e.closedAt,
      closeReason: e.closeReason,
      intentional: e.intentional,
      reconnects: e.reconnects,
      disconnects: e.disconnects,
      lastStatus: e.lastStatus,
      lastStatusAt: e.lastStatusAt,
      ...stats,
    });
  }
  connections.reverse();
  return {
    open: openCount(st),
    overlapSince: st.overlapSince,
    overlapReports: st.overlapReports,
    peakOpen: st.peakOpen,
    attempts: st.attempts,
    opened: st.opened,
    failed: st.failed,
    closed: st.closed,
    unexpectedCloses: st.unexpectedCloses,
    reconnects,
    connections,
  };
}

function readStats(nc: LedgerConnectionLike | null): {
  readonly inMsgs: number;
  readonly outMsgs: number;
  readonly inBytes: number;
  readonly outBytes: number;
} {
  const zero = { inMsgs: 0, outMsgs: 0, inBytes: 0, outBytes: 0 };
  if (nc === null || typeof nc.stats !== 'function') return zero;
  try {
    const s = nc.stats();
    return {
      inMsgs: s.inMsgs ?? 0,
      outMsgs: s.outMsgs ?? 0,
      inBytes: s.inBytes ?? 0,
      outBytes: s.outBytes ?? 0,
    };
  } catch {
    return zero;
  }
}

/** Listener fires on every lifecycle change (not on traffic; poll for stats). */
export function subscribeConnectionLedger(listener: () => void): () => void {
  const st = state();
  st.listeners.add(listener);
  return (): void => {
    st.listeners.delete(listener);
  };
}

export function _resetConnectionLedgerForTests(opts?: { readonly overlapGraceMs?: number }): void {
  const previous = (globalThis as GlobalWithLedger)[CONNECTION_LEDGER_GLOBAL_KEY];
  if (previous?.overlapTimer != null) clearTimeout(previous.overlapTimer);
  const next = freshState();
  if (opts?.overlapGraceMs !== undefined) next.overlapGraceMs = opts.overlapGraceMs;
  (globalThis as GlobalWithLedger)[CONNECTION_LEDGER_GLOBAL_KEY] = next;
}
